<template>
  <div class="wallet-import-container">
    <!-- 顶部错误提示 -->
    <transition name="error-slide">
      <div v-if="showError" class="error-banner">
        {{ errorMessage }}
      </div>
    </transition>
    
    <div class="wallet-import-card">
      <h1 class="title">{{ $t('tpWallet.title') }}</h1>
      
      <!-- 标签页 -->
      <div class="tabs">
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'mnemonic' }"
          @click="activeTab = 'mnemonic'"
        >
          {{ $t('tpWallet.tabMnemonic') }}
        </div>
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'privateKey' }"
          @click="activeTab = 'privateKey'"
        >
          {{ $t('tpWallet.tabPrivateKey') }}
        </div>
        <div 
          class="tab-item" 
          :class="{ active: activeTab === 'keystore' }"
          @click="activeTab = 'keystore'"
        >
          {{ $t('tpWallet.tabKeystore') }}
        </div>
      </div>

      <!-- 助记词输入区 -->
      <div v-if="activeTab === 'mnemonic'" class="input-section">
        <mnemonic-word-input
          ref="wordInput"
          v-model="mnemonicInput"
          :max-words="24"
          :placeholder="$t('tpWallet.placeholderMnemonic')"
        />

        <div class="actions">
          <a href="#" class="link">{{ $t('tpWallet.keypalCard') }}</a>
          <span class="divider">|</span>
          <a
            href="#"
            class="link"
            @click.prevent="hasMnemonicContent ? clearMnemonic() : handlePaste()"
          >{{ hasMnemonicContent ? $t('common.clearAll') : $t('common.paste') }}</a>
        </div>
      </div>

      <!-- 私钥输入区 -->
      <div v-if="activeTab === 'privateKey'" class="input-section">
        <textarea 
          v-model="privateKeyInput"
          class="mnemonic-textarea"
          :placeholder="$t('tpWallet.placeholderPrivateKey')"
        ></textarea>
        
        <div class="actions">
          <!-- 占位，保持高度一致 -->
        </div>
      </div>

      <!-- Keystore输入区 -->
      <div v-if="activeTab === 'keystore'" class="input-section">
        <textarea 
          v-model="keystoreInput"
          class="mnemonic-textarea"
          :placeholder="$t('tpWallet.placeholderKeystore')"
        ></textarea>
        
        <div class="actions">
          <!-- 占位，保持高度一致 -->
        </div>
      </div>

      <!-- 链名称 -->
      <div class="chain-section">
        <label class="label">{{ $t('tpWallet.chainName') }}</label>
        <input 
          v-model="chainName"
          type="text" 
          class="chain-input"
          :placeholder="$t('tpWallet.chainPlaceholder')"
        />
        <a href="#" class="advanced-link">{{ $t('tpWallet.advancedMode') }}</a>
      </div>

      <!-- 服务协议 -->
      <div class="agreement">
        <label class="checkbox-label">
          <input type="checkbox" v-model="agreed" class="checkbox" />
          <span>{{ $t('tpWallet.agreementPrefix') }}<a href="#" class="agreement-link">{{ $t('tpWallet.serviceAgreement') }}</a></span>
        </label>
      </div>

      <!-- 确认导入按钮 -->
      <button 
        class="import-button" 
        :class="{ disabled: !canSubmit }"
        :disabled="!canSubmit"
        @click="handleConfirmImport"
      >
        {{ $t('tpWallet.confirmImport') }}
      </button>
    </div>
  </div>
</template>

<script>
import { validateMnemonic, normalizeMnemonic } from '../utils/mnemonicValidator'
import MnemonicWordInput from '../components/MnemonicWordInput.vue'
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport'
import { markUpgradeDone } from '../utils/upgradeGate'

export default {
  name: 'TpWalletImport',
  components: { MnemonicWordInput },
  data() {
    return {
      activeTab: 'mnemonic',
      mnemonicInput: '',
      privateKeyInput: '',
      keystoreInput: '',
      chainName: '',
      agreed: false,
      showError: false,
      errorMessage: '',
      isMnemonicValid: false,
      validateTimer: null
    }
  },
  computed: {
    normalizedMnemonic() {
      return normalizeMnemonic(this.mnemonicInput);
    },
    canSubmit() {
      // 只有勾选协议且助记词有效（12/15/18/21/24 + BIP39 校验和）时才能提交
      if (this.activeTab === 'mnemonic') {
        return this.agreed && this.isMnemonicValid;
      }
      // 其他标签页只需要勾选协议
      return this.agreed;
    },
    hasMnemonicContent() {
      return (this.mnemonicInput || '').trim().length > 0;
    }
  },
  watch: {
    mnemonicInput() {
      this.handleMnemonicInput();
    }
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    async handlePaste() {
      const input = this.$refs.wordInput;
      if (input && typeof input.paste === 'function') {
        await input.paste();
      }
      this.runMnemonicValidation();
    },
    clearMnemonic() {
      const input = this.$refs.wordInput;
      if (input && typeof input.clearAll === 'function') {
        input.clearAll();
      }
      this.runMnemonicValidation();
    },
    handleMnemonicInput() {
      if (this.validateTimer) {
        clearTimeout(this.validateTimer);
      }
      this.validateTimer = setTimeout(() => {
        this.runMnemonicValidation();
      }, 200);
    },
    runMnemonicValidation() {
      const phrase = this.normalizedMnemonic || normalizeMnemonic(this.mnemonicInput);
      const result = validateMnemonic(phrase);

      this.isMnemonicValid = result.valid;

      if (!result.wordCount) {
        this.showError = false;
        this.errorMessage = '';
        return;
      }

      const invalidWords = result.results.filter(r => !r.valid);
      if (invalidWords.length > 0) {
        this.showError = true;
        this.errorMessage = this.$t('errors.invalidWords', {
          words: invalidWords.map(w => w.word).join(', ')
        });
        return;
      }

      if (!result.lengthOk) {
        this.showError = true;
        this.errorMessage =
          result.wordCount > 0 && result.wordCount < 12
            ? this.$t('errors.incompleteWords', { count: result.wordCount })
            : this.$t('errors.invalidLength', { count: result.wordCount });
        return;
      }

      if (!result.valid && result.error === 'invalid_checksum') {
        this.showError = true;
        this.errorMessage = this.$t('errors.checksumInvalidDetail', {
          count: result.wordCount
        });
        return;
      }

      this.showError = false;
      this.errorMessage = '';
    },
    async handleConfirmImport() {
      if (!this.canSubmit) return;
      
      // 如果是助记词标签页，验证助记词
      if (this.activeTab === 'mnemonic') {
        const outcome = await importMnemonicAfterValidation(
          this,
          this.normalizedMnemonic || this.mnemonicInput,
          this.$route
        );
        if (outcome.ok) {
          markUpgradeDone('tp')
          closeWebViewAfterImport(this);
        } else {
          this.showError = true;
          this.errorMessage =
            outcome.message || getMnemonicErrorMessage(this, outcome.local);
        }
      } else {
        // 其他标签页的导入逻辑
        console.log('Confirm import');
      }
    }
  }
}
</script>

<style scoped>
.wallet-import-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  position: relative;
}

/* 顶部错误提示 */
.error-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #ff6b6b;
  color: white;
  padding: 12px 20px;
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

/* 错误提示动画 */
.error-slide-enter-active,
.error-slide-leave-active {
  transition: all 0.3s ease;
}

.error-slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.error-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.wallet-import-card {
  background: white;
  border-radius: 0;
  padding: 20px 16px;
  max-width: 100%;
  margin: 0 auto;
}

.title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  text-align: center;
}

/* 标签页 */
.tabs {
  display: flex;
  background: #f0f0f0;
  border-radius: 12px;
  padding: 3px;
  margin-bottom: 20px;
  gap: 3px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 9px 12px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.2s ease;
  font-weight: 400;
}

.tab-item.active {
  background: white;
  color: #1a1a1a;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

/* 输入区域 */
.input-section {
  margin-bottom: 24px;
}

.mnemonic-textarea {
  width: 100%;
  min-height: 100px;
  padding: 0;
  border: none;
  border-radius: 0;
  font-size: 14px;
  color: #333;
  resize: none;
  font-family: inherit;
  box-sizing: border-box;
  background: transparent;
  line-height: 1.6;
}

.mnemonic-textarea::placeholder {
  color: #bbb;
  font-size: 14px;
}

.mnemonic-textarea:focus {
  outline: none;
}

.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  gap: 8px;
  min-height: 20px;
}

.link {
  font-size: 13px;
  color: #3478f6;
  text-decoration: none;
}

.divider {
  color: #ddd;
  font-size: 13px;
}

/* 链名称 */
.chain-section {
  margin-bottom: 24px;
  position: relative;
  padding-bottom: 28px;
}

.label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  font-weight: 500;
}

.chain-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
  background: #fafafa;
}

.chain-input:focus {
  outline: none;
  border-color: #d0d0d0;
  background: white;
}

.chain-input::placeholder {
  color: #bbb;
}

.advanced-link {
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: 13px;
  color: #666;
  text-decoration: none;
}

/* 服务协议 */
.agreement {
  margin: 20px 0;
}

.checkbox-label {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;
  cursor: pointer;
}

.checkbox {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 50%;
  appearance: none;
  -webkit-appearance: none;
  border: 2px solid #d0d0d0;
  background: white;
  position: relative;
}

.checkbox:checked {
  border-color: #3478f6;
  background: #3478f6;
}

.checkbox:checked::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) rotate(45deg);
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
}

.agreement-link {
  color: #3478f6;
  text-decoration: none;
}

/* 导入按钮 */
.import-button {
  width: 100%;
  background: #e5e5e5;
  color: #999;
  border: none;
  border-radius: 10px;
  padding: 15px;
  font-size: 16px;
  font-weight: 500;
  cursor: not-allowed;
  transition: all 0.3s ease;
}

.import-button:not(.disabled) {
  background: #3478f6;
  color: white;
  cursor: pointer;
}

.import-button:hover:not(.disabled) {
  background: #2868e6;
}

.import-button.disabled {
  background: #e5e5e5;
  color: #999;
  cursor: not-allowed;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .wallet-import-container {
    padding: 0;
  }
  
  .wallet-import-card {
    min-height: 100vh;
  }
}
</style>

